/** Default column widths (px) — sized so full header label + filter icon fit */
export const DEFAULT_COLUMN_WIDTHS = {
  delete: 60,
  application_number: 245,
  originator_name: 165,
  borrower_name: 150,
  property_address: 175,
  created_by: 150,
  created_date: 150,
  updated_date: 150,
  cx_specialist_name: 150,
  application_status_name: 150,
  is_test_app: 155,
  submitted_to_crm: 165,
  is_submitted_by_partner_api: 230,
  is_upside_submission: 185,
  is_upside_interested: 185,
  property_type: 150,
  company_name: 150,
  program_name: 150,
  purchase_price: 150,
  purchase_date: 150,
  owner_estimated_value: 175,
  borrower_email: 150,
  co_borrower_name: 155,
  co_borrower_email: 165,
  loan_officer_name: 155,
  loan_officer_email: 165,
  real_estate_agent_name: 175,
  real_estate_agent_email: 185,
  partner_loan_number: 165,
  initial_submiter_role: 165,
  initiail_submiter_name: 175,
  initial_submiter_email: 175,
  home_owner_approved: 155,
  home_owner_approval_date: 185,
  is_primary_residence: 165,
  equity_unlock_requested: 185,
};

/** Header labels used to compute collapse min width */
export const COLUMN_HEADER_LABELS = {
  delete: "Delete",
  application_number: "Trade In Application Number",
  originator_name: "Originator Name",
  borrower_name: "Borrower Name",
  property_address: "Property Address",
  created_by: "Created By",
  created_date: "Created Date",
  updated_date: "Updated Date",
  cx_specialist_name: "CX Specialist",
  application_status_name: "Status",
  is_test_app: "Test Application",
  submitted_to_crm: "Submitted to CRM",
  is_submitted_by_partner_api: "Submitted by Partner API",
  is_upside_submission: "Is Upside Submission",
  is_upside_interested: "Is Upside Interested",
  property_type: "Property Type",
  company_name: "Company Name",
  program_name: "Program Name",
  purchase_price: "Purchase Price",
  purchase_date: "Purchase Date",
  owner_estimated_value: "Owner Estimated Value",
  borrower_email: "Borrower Email",
  co_borrower_name: "Co-Borrower Name",
  co_borrower_email: "Co-Borrower Email",
  loan_officer_name: "Loan Officer Name",
  loan_officer_email: "Loan Officer Email",
  real_estate_agent_name: "Real Estate Agent Name",
  real_estate_agent_email: "Real Estate Agent Email",
  partner_loan_number: "Partner Loan Number",
  initial_submiter_role: "Initial Submitter Role",
  initiail_submiter_name: "Initial Submitter Name",
  initial_submiter_email: "Initial Submitter Email",
  home_owner_approved: "Homeowner Approved",
  home_owner_approval_date: "Homeowner Approval Date",
  is_primary_residence: "Is Primary Residence",
  equity_unlock_requested: "Equity Unlock Requested",
};

const MAX_COLUMN_WIDTH = 480;
const HEADER_CHAR_PX = 8.2;
const HEADER_ELLIPSIS_PX = 12;
// filter icon + sort icon + padding + gaps inside header cell
const HEADER_CHROME_PX = 62;

/**
 * Collapse min width:
 * - short column names (≤ 8 chars, ignoring spaces) → keep ~3 letters
 * - longer names → keep ~6 letters (e.g. "Propert...")
 * - Created By only → allow ~6 letters (no large sticky floor)
 */
export const getMinColumnWidth = (colId) => {
  if (colId === "delete") return DEFAULT_COLUMN_WIDTHS.delete;
  // Created By: collapse to ~6 visible letters (e.g. "Create..." / "calque...")
  if (colId === "created_by") {
    return Math.round(28 + 6 * HEADER_CHAR_PX + HEADER_ELLIPSIS_PX);
  }
  const label = COLUMN_HEADER_LABELS[colId] || "";
  const compactLen = label.replace(/\s/g, "").length;
  const visibleChars = compactLen > 0 && compactLen <= 8 ? 3 : 6;
  return Math.round(
    HEADER_CHROME_PX + visibleChars * HEADER_CHAR_PX + HEADER_ELLIPSIS_PX,
  );
};

/** @deprecated use getMinColumnWidth(colId) — kept for callers expecting a number */
export const COLUMN_WIDTH_MIN = getMinColumnWidth("originator_name");

export const clampColumnWidth = (width, colId) => {
  const min = colId ? getMinColumnWidth(colId) : COLUMN_WIDTH_MIN;
  return Math.max(min, Math.min(MAX_COLUMN_WIDTH, width));
};

export const getColumnWidth = (colId, columnWidths) => {
  if (colId === "delete") return DEFAULT_COLUMN_WIDTHS.delete;
  const width = columnWidths?.[colId] ?? DEFAULT_COLUMN_WIDTHS[colId] ?? 120;
  return clampColumnWidth(width, colId);
};

export const mergeColumnWidthIntoStyle = (colId, baseStyle, columnWidths) => {
  if (colId === "delete") return baseStyle;
  const width = getColumnWidth(colId, columnWidths);
  return {
    ...baseStyle,
    width,
    minWidth: width,
    maxWidth: width,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  };
};

const NON_DRAGGABLE_COLUMN_IDS = ["delete", "application_number"];

/** Shown in Change Column drawer — always checked, checkbox disabled */
export const LOCKED_DRAWER_COLUMN_IDS = [
  "application_number",
  "originator_name",
  "borrower_name",
  "property_address",
  "created_by",
];

/** Never shown in Change Column drawer */
export const HIDDEN_DRAWER_COLUMN_IDS = ["delete"];

/** Always stay checked in the table (hidden from drawer, not user-toggleable) */
export const ALWAYS_CHECKED_COLUMN_IDS = ["delete"];

/** Fixed position in drawer/table — cannot drag to reorder */
export const DRAWER_NON_DRAGGABLE_COLUMN_IDS = ["application_number"];

export const isDrawerLockedColumn = (colId) =>
  LOCKED_DRAWER_COLUMN_IDS.includes(colId);

export const isDrawerVisibleColumn = (colId) =>
  !HIDDEN_DRAWER_COLUMN_IDS.includes(colId);

export const isDrawerNonDraggable = (colId) =>
  DRAWER_NON_DRAGGABLE_COLUMN_IDS.includes(colId);

export const isAlwaysCheckedColumn = (colId) =>
  ALWAYS_CHECKED_COLUMN_IDS.includes(colId);

/** Reorder visible columns via HTML5 drag-drop; Delete + App Number stay first. */
export const reorderColumnsByDrag = (allColumns, dragId, dropId) => {
  if (!dragId || !dropId || dragId === dropId) return allColumns;
  if (
    NON_DRAGGABLE_COLUMN_IDS.includes(dragId) ||
    NON_DRAGGABLE_COLUMN_IDS.includes(dropId)
  ) {
    return allColumns;
  }

  const visible = allColumns
    .filter((c) => c.checked)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const from = visible.findIndex((c) => c.id === dragId);
  const to = visible.findIndex((c) => c.id === dropId);
  if (from < 0 || to < 0) return allColumns;

  const nextVisible = [...visible];
  const [moved] = nextVisible.splice(from, 1);
  nextVisible.splice(to, 0, moved);

  const pinned = NON_DRAGGABLE_COLUMN_IDS.map((id) =>
    nextVisible.find((c) => c.id === id),
  ).filter(Boolean);
  const rest = nextVisible.filter(
    (c) => !NON_DRAGGABLE_COLUMN_IDS.includes(c.id),
  );
  const orderedVisible = [...pinned, ...rest];

  const unchecked = allColumns
    .filter((c) => !c.checked)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  return [
    ...orderedVisible.map((c, i) => ({ ...c, orderIndex: i })),
    ...unchecked.map((c, i) => ({
      ...c,
      orderIndex: orderedVisible.length + i,
    })),
  ];
};

/** Reorder Change Column drawer list; Delete + App Number stay fixed in table order. */
export const reorderDrawerColumnsByDrag = (allColumns, dragId, dropId) => {
  if (!dragId || !dropId || dragId === dropId) return allColumns;
  if (
    isDrawerNonDraggable(dragId) ||
    isDrawerNonDraggable(dropId)
  ) {
    return allColumns;
  }

  const sorted = [...allColumns].sort(
    (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0),
  );

  const deleteCol = sorted.find((c) => c.id === "delete");
  const appNumCol = sorted.find((c) => c.id === "application_number");
  const reorderable = sorted.filter(
    (c) => c.id !== "delete" && c.id !== "application_number",
  );

  const from = reorderable.findIndex((c) => c.id === dragId);
  const to = reorderable.findIndex((c) => c.id === dropId);
  if (from < 0 || to < 0) return allColumns;

  const nextReorderable = [...reorderable];
  const [moved] = nextReorderable.splice(from, 1);
  nextReorderable.splice(to, 0, moved);

  const ordered = [deleteCol, appNumCol, ...nextReorderable].filter(Boolean);
  return ordered.map((c, i) => ({ ...c, orderIndex: i }));
};

/** Filter popup config per column id */
export const COLUMN_FILTER_CONFIG = {
  application_number: { fieldType: "appnumber" },
  originator_name: {},
  borrower_name: {},
  property_address: {},
  created_date: { fieldType: "daterange" },
  updated_date: { fieldType: "dateon" },
  cx_specialist_name: {},
  application_status_name: {},
  is_test_app: { fieldType: "boolean" },
  submitted_to_crm: { fieldType: "boolean" },
  is_submitted_by_partner_api: { fieldType: "boolean" },
  is_upside_submission: { fieldType: "boolean" },
  is_upside_interested: { fieldType: "boolean", showNotAvailableOption: true },
  property_type: { textFilterVariant: "full" },
  company_name: { textFilterVariant: "full" },
  program_name: { textFilterVariant: "full" },
  purchase_price: { fieldType: "numericrange" },
  purchase_date: {
    fieldType: "daterange",
    hideExactOn: true,
    showHasDateBoolean: true,
  },
  owner_estimated_value: { fieldType: "numericrange" },
  borrower_email: { textFilterVariant: "full" },
  co_borrower_name: { textFilterVariant: "full" },
  co_borrower_email: { textFilterVariant: "full" },
  loan_officer_name: { textFilterVariant: "full" },
  loan_officer_email: { textFilterVariant: "full" },
  real_estate_agent_name: { textFilterVariant: "full" },
  real_estate_agent_email: { textFilterVariant: "full" },
  partner_loan_number: { fieldType: "exactcontains" },
  initial_submiter_role: { textFilterVariant: "full" },
  initiail_submiter_name: { textFilterVariant: "full" },
  initial_submiter_email: { textFilterVariant: "full" },
  home_owner_approved: { fieldType: "boolean" },
  home_owner_approval_date: { fieldType: "daterange", hideExactOn: true },
  is_primary_residence: { fieldType: "boolean" },
  equity_unlock_requested: { fieldType: "numericrange" },
};

export const getColumnCellValue = (colId, row) => {
  switch (colId) {
    case "application_number":
      return row.trade_in_application_number;
    case "originator_name":
      return row.originator_name?.partner_name ?? "-";
    case "borrower_name":
      return row.borrower?.name ?? "-";
    case "property_address":
      return row.property_address ?? "-";
    case "created_by":
      return row.created_by ?? "-";
    case "created_date":
      return row.created_date
        ? new Date(row.created_date).toLocaleDateString("en-US")
        : "NA";
    case "updated_date":
      return row.updated_date
        ? new Date(row.updated_date).toLocaleDateString("en-US")
        : "NA";
    case "cx_specialist_name":
      return row?.cx_specialist === null
        ? "-"
        : row?.cx_specialist?.get_user_full_name ?? "-";
    case "application_status_name":
      return row.app_status ?? "-";
    case "is_test_app":
      return row.is_test_app === false ? "No" : "Yes";
    case "submitted_to_crm":
      return row.submitted_to_crm === false ? "No" : "Yes";
    case "is_submitted_by_partner_api":
      return row.is_submitted_by_partner_api === false ? "No" : "Yes";
    case "is_upside_submission":
      return row.is_upside_submission === false ? "No" : "Yes";
    case "is_upside_interested":
      return row.is_upside_interested === true
        ? "Yes"
        : row.is_upside_interested === false
          ? "No"
          : "-";
    case "property_type":
      return row.property_type ?? "-";
    case "company_name":
      return row.submitter_company_name ?? "-";
    case "program_name":
      return row.calque_program ?? "-";
    case "purchase_price":
      return row.purchase_price ?? "-";
    case "purchase_date":
      return row.purchase_date
        ? new Date(row.purchase_date).toLocaleDateString("en-US")
        : "-";
    case "owner_estimated_value":
      return row.owner_estimated_home_value ?? "-";
    case "borrower_email":
      return row.borrower?.email ?? "-";
    case "co_borrower_name":
      return row.co_borrower?.name ?? "-";
    case "co_borrower_email":
      return row.co_borrower?.email ?? "-";
    case "loan_officer_name":
      return row.loan_officer_name ?? "-";
    case "loan_officer_email":
      return row.loan_officer_email ?? "-";
    case "real_estate_agent_name":
      return row.real_estate_agent_name ?? "-";
    case "real_estate_agent_email":
      return row.real_estate_agent_email ?? "-";
    case "partner_loan_number":
      return row.external_reference_number ?? "-";
    case "initial_submiter_role":
      return row.submitter_role ?? "-";
    case "initiail_submiter_name":
      return row.submitter_name ?? "-";
    case "initial_submiter_email":
      return row.submitter_email ?? "-";
    case "home_owner_approved":
      return row.ho_approved === true
        ? "Yes"
        : row.ho_approved === false
          ? "No"
          : "-";
    case "home_owner_approval_date":
      return row.homeowner_approved_date
        ? new Date(row.homeowner_approved_date).toLocaleDateString("en-US")
        : "-";
    case "is_primary_residence":
      return row.is_primary_residence === true
        ? "Yes"
        : row.is_primary_residence === false
          ? "No"
          : "-";
    case "equity_unlock_requested":
      return row.equity_unlock_requested ?? "-";
    default:
      return "";
  }
};

export const getHeaderStickyStyle = (colId, columnWidths = {}) => {
  if (colId === "delete") {
    return {
      position: "sticky",
      left: 0,
      zIndex: 4,
      backgroundColor: "#f4f5f6",
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      borderLeft: "1px solid #e8e8e8",
      borderRight: "1px solid #e8e8e8",
    };
  }
  if (colId === "application_number") {
    const deleteWidth = getColumnWidth("delete", columnWidths);
    return {
      position: "sticky",
      left: deleteWidth,
      zIndex: 3,
      backgroundColor: "#f4f5f6",
      borderRight: "1px solid #e8e8e8",
      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.05)",
    };
  }
  const minWidths = {
    originator_name: 150,
    borrower_name: 130,
    property_address: 180,
  };
  return {
    position: "relative",
    minWidth: minWidths[colId] || undefined,
  };
};

export const getBodyStickyStyle = (colId, stickyBg, columnWidths = {}) => {
  if (colId === "delete") {
    return {
      position: "sticky",
      left: 0,
      zIndex: 3,
      backgroundColor: stickyBg,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      borderLeft: "1px solid #e8e8e8",
      borderRight: "1px solid #e8e8e8",
    };
  }
  if (colId === "application_number") {
    const deleteWidth = getColumnWidth("delete", columnWidths);
    return {
      position: "sticky",
      left: deleteWidth,
      zIndex: 2,
      backgroundColor: stickyBg,
      borderRight: "1px solid #e8e8e8",
      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.05)",
    };
  }
  const minWidths = {
    originator_name: 150,
    borrower_name: 130,
    property_address: 180,
  };
  return { minWidth: minWidths[colId] || undefined };
};
